/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Apps from '@react/react-spectrum/Icon/Apps';
import BadgeIcon from './Badge/BadgeIcon';
import Button from '@react/react-spectrum/Button';
import classNames from 'classnames';
import {getOmegaEvent} from './utils/utils';
import {getOrgDisplayName} from './utils/utils';
import Help from '@react/react-spectrum/Icon/Help';
import {HelpPopover} from '@exc/help-center';
import metrics from '@exc/runtime/metrics';
import PropTypes from 'prop-types';
import Pulse from './Pulse';
import React from 'react';
import Search from '@react/react-spectrum/Icon/Search';
import ShellAvatar from './ShellAvatar';
import ShellHeader from './ShellHeader';
import ShellOrgSwitcherPopover from './ShellOrgSwitcherPopover';
import ShellOverlayTrigger from './ShellOverlayTrigger';
import ShellUserProfilePopover from './ShellUserProfilePopover';
import SolutionPopover from './SolutionSwitcher/SolutionPopover';
import {withLocalization} from '@adobe/adobe-intl-react';
import './Shell.scss';

const MEDIUM_MAX_WIDTH = 900;
const SMALL_MAX_WIDTH = 700;

/**
 * Shell Super component for Experience Cloud UI.
 */
class Shell extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isNotOutlined: true,
      size: 'L'
    };
    this.metrics = metrics.create('exc.shell.Shell');
  }

  /**
   * Handler for opening and closing Help Center menu.
   * Added for specific case of opening Help menu from solution.
   * @param Event Event object.
   * @param boolean Flag for differentiating explicitly closing with onHide callback.
   */
  onToggleHelpCenter = (e, shouldClose) => {
    let helpCenterProps = this.props.helpCenterConfig;
    const {config, selectedTab} = e && e.detail || {};
    if (config && selectedTab) {
      helpCenterProps = Object.assign({}, helpCenterProps, {
        config,
        open: true,
        selectedTab
      });
    } else {
      helpCenterProps = Object.assign({}, helpCenterProps, {
        open: shouldClose ? false : this.state.helpCenterProps && !this.state.helpCenterProps.open
      });
    }
    this.setState({helpCenterProps});
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    document.addEventListener('openHelpCenter', this.onToggleHelpCenter);
    this.onResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('openHelpCenter', this.onToggleHelpCenter);
  }

  /**
   * Handler for browser resize event.
   * @listens Event window's resize event.
   */
  onResize = () => {
    const width = window.innerWidth;
    const {size} = this.state;
    let newSize;

    if (width > MEDIUM_MAX_WIDTH) {
      newSize = 'L';
    } else if (width <= SMALL_MAX_WIDTH) {
      newSize = 'S';
    } else {
      newSize = 'M';
    }

    if (newSize !== size) {
      this.setState({size: newSize});
    }
  };

  /**
   * Shows feedback dialog.
   */
  showFeedbackTool = (event) => {
    const {feedback} = this.props;
    this.metrics.analytics.trackEvent(getOmegaEvent({
      attributes: {label: feedback.buttonLabel, type: feedback.type},
      element: 'feedback button',
      event,
      widget: {name: 'feedback', type: 'button'}
    }));

    switch (feedback.type) {
      case 'custom':
        break;
      case 'external':
        window.open(feedback.url, '_blank');
        break;
      case 'openFeedback':
        document.dispatchEvent(new CustomEvent('openHelpCenter', {
          detail: {
            config: {
              subject: feedback.subject || this.props.solutionTitle || 'Adobe Experience Cloud',
              type: 'BETA_FEEDBACK_SUBMISSION'
            },
            selectedTab: 'feedback'
          }
        }));
        break;
      default:
        return;
    }
  }

  /**
   * Create Aria-label for the Notification/Announcement Button.
   * @returns {string} Label for Pulse notifications and announcements.
   */
  getPulseLabel() {
    const {formatMessage, pulse} = this.props;
    let label = formatMessage({
      defaultMessage: 'Notifications and Announcements',
      description: 'Label for the Notifications and Announcements Button',
      messageId: 'notification'});
    if (pulse.unreadNotificationsCount) {
      label += `. ${formatMessage({
        defaultMessage: 'unread notifications',
        description: 'Label for unread notifications',
        messageId: 'unread'})} : ${pulse.unreadNotificationsCount} `;
    }
    if (pulse.hasNewAnnouncement) {
      label += `. ${formatMessage({
        defaultMessage: 'Has new announcement',
        description: 'New Announcement Label for Notification Button',
        messageId: 'newAnnouncement'})}`;
    }
    return label;
  }

  /**
   * Checks if environment is in devmode using query param.
   * @returns {boolean} Whether or not shell is in devmode.
   */
  isDevmode() {
    let localConfigs = JSON.parse(window.localStorage.getItem('unifiedShellConfig'));
    let devmodeEnabled = localConfigs && localConfigs.devmodeEnabled === true;
    return this.props.getQueryValue('devmode') === 'true' || devmodeEnabled;
  }

  /**
   * Gets proper label based on dev environment.
   * @returns {string} Proper string for dev environment label.
   */
  getEnvironmentLabel() {
    const {environment} = this.props;

    if (this.isDevmode()) {
      return 'Dev Mode';
    }

    switch (environment) {
      case 'dev':
        return 'Dev';
      case 'qa':
        return 'QA';
      case 'stage':
        return 'Staging';
      case 'test':
        return 'Test';
    }
  }
  render() {
    const {
      children,
      container,
      customProfileButtons,
      customSearch,
      currentActiveTenantId,
      customEnvLabel,
      feedback,
      formatMessage,
      helpCenterConfig,
      imsOrgs,
      landingpage,
      locale,
      localeData,
      modal,
      onCustomProfileButtonClick,
      onImsOrgChange,
      onSignOutClick,
      onSolutionClick,
      onSolutionSwitcherClick,
      onUserProfileClick,
      onWorkspaceChange,
      pulse,
      selectedImsOrg,
      selectedSubOrg,
      selectedWorkspaceId,
      services,
      showBadge,
      showLearnMoreModal,
      solutionIcon,
      solutions,
      solutionShortTitle,
      solutionTitle,
      orgToSubOrgsMap,
      supportsViewportTakeover,
      translationService,
      userAvatarUrl,
      userFirstName,
      userLastName,
      userName,
      userTitle,
      viewportTakeover,
      workspaces
    } = this.props;

    // NOTE: This is here so that the unread change function is provided to
    // pulse and the badge will show for new notifications.
    // DO NOT REMOVE UNTIL WE REFACTOR PULSE IMPLEMENTATION!!
    showBadge && pulse.pulseComponent.flyoutComponent(pulse.onPulseUnreadChange);
    const {helpCenterProps, size} = this.state;
    const envLabel = this.getEnvironmentLabel();
    const actions = [
      <ShellOverlayTrigger
        className="solution-switcher-menu"
        element="solution switcher"
        key="solution-menu"
        placement="bottom"
        rootClose={false}>
        <Button
          aria-label={formatMessage({
            defaultMessage: 'Switch to another Solution',
            description: 'Label for Solution Switcher',
            messageId: 'solutionSwitcher'})}
          className="solution-menu shellSolutionSwitcher"
          variant="tool">
          <Apps size="S" />
        </Button>
        <SolutionPopover
          landingpage={landingpage}
          locale={locale}
          localeData={localeData}
          onSolutionClick={onSolutionSwitcherClick}
          services={services}
          showLearnMoreModal={showLearnMoreModal}
          solutions={solutions}
        />
      </ShellOverlayTrigger>,
      customSearch.enabled && (
        <span
          className={classNames({'spectrum-Shell-custom-menu--open': customSearch.open})}
          key="custom-search"
        >
          <Button
            aria-label={formatMessage({
              defaultMessage: 'Search',
              description: 'Label for search button',
              messageId: 'searchButton'})}
            icon={<Search />}
            onClick={customSearch.onClick}
            quiet
            selected={customSearch.open}
            variant="tool" />
        </span>
      ),
      <ShellOverlayTrigger
        element="help center"
        key="help-menu"
        onHide={(e) => this.onToggleHelpCenter(e, true)}
        placement="bottom"
        show={helpCenterProps && helpCenterProps.open}
        trigger="click">
        <Button
          aria-label={formatMessage({
            defaultMessage: 'Help',
            description: 'Help header',
            messageId: 'help'})}
          className="help-menu"
          onClick={this.onToggleHelpCenter}
          variant="tool">
          <Help size="S"/>
        </Button>
        <HelpPopover
          {...helpCenterProps || helpCenterConfig}
          locale={locale}
          localeData={localeData}
          translationService={translationService} />
      </ShellOverlayTrigger>,
      showBadge && (
        <ShellOverlayTrigger
          animateFrom="top"
          container={container}
          element="pulse"
          full
          key="notifications-badge"
          onHide={pulse.onPulseClose}
          onShow={pulse.onPulseOpen}
          placement="bottom"
          trigger="click">
          <Button
            aria-label={this.getPulseLabel()}
            className="pulse-menu"
            variant="tool"
          >
            <BadgeIcon
              count={pulse.unreadNotificationsCount}
              hasNewAnnouncement={pulse.hasNewAnnouncement}
              key="pulse-badge"
              maxDigits={1}
            />
          </Button>
          <Pulse
            component={pulse.pulseComponent}
            onUnreadChange={pulse.onPulseUnreadChange}
            onViewAll={pulse.onPulseViewAllClick}
          />
        </ShellOverlayTrigger>
      ),
      <ShellOverlayTrigger
        element="user profile"
        key="profile-menu"
        placement="bottom">
        <Button
          aria-label={formatMessage({
            defaultMessage: 'Profile',
            description: 'Profile Button Label',
            messageId: 'profile'})}
          className="solution-switcher-avatar"
          variant="tool">
          {userAvatarUrl ?
            <ShellAvatar src={userAvatarUrl} /> :
            <span />}
        </Button>
        <ShellUserProfilePopover
          avatarUrl={userAvatarUrl}
          currentActiveTenantId={currentActiveTenantId}
          customProfileButtons={customProfileButtons}
          firstName={userFirstName}
          formatMessage={formatMessage}
          hasImsOrgSwitcher={size === 'S' || (imsOrgs && imsOrgs.length === 1)}
          imsOrgs={imsOrgs}
          lastName={userLastName}
          locale={locale}
          localeData={localeData}
          name={userName}
          onCustomProfileButtonClick={onCustomProfileButtonClick}
          onImsOrgChange={onImsOrgChange}
          onProfileClick={onUserProfileClick}
          onSignOutClick={onSignOutClick}
          orgToSubOrgsMap={orgToSubOrgsMap}
          selectedImsOrg={selectedImsOrg}
          selectedSubOrg={selectedSubOrg}
          subheading={userTitle}
        />
      </ShellOverlayTrigger>
    ];

    // Include the switcher when:
    // 1. size is not small
    // 2. we have > 1 orgs
    // 3. we have a selected org
    // 4. we have 1 org and > 1 sub orgs
    let hasMultipleSubOrgs = orgToSubOrgsMap && imsOrgs[0] && orgToSubOrgsMap[imsOrgs[0].value] && orgToSubOrgsMap[imsOrgs[0].value].length > 1;
    if (size !== 'S' && imsOrgs && (imsOrgs.length > 1 || hasMultipleSubOrgs) && selectedImsOrg) {
      actions.unshift(
        <ShellOverlayTrigger
          className="spectrum-Shell-orgMenu"
          element="ims org switcher"
          key="org-menu"
          placement="bottom"
        >
          <Button className="spectrum-Shell-orgButton" variant="tool">
            {
              (Object.keys(orgToSubOrgsMap).length && selectedSubOrg) &&
                selectedSubOrg.name || getOrgDisplayName(imsOrgs, selectedImsOrg)
            }
          </Button>
          <ShellOrgSwitcherPopover
            imsOrgs={imsOrgs}
            locale={locale}
            onImsOrgChange={onImsOrgChange}
            orgToSubOrgsMap={orgToSubOrgsMap}
            selectedImsOrg={selectedImsOrg}
            selectedSubOrg={selectedSubOrg}
          />
        </ShellOverlayTrigger>
      );
    }

    if (size !== 'S' && feedback && feedback.enabled) {
      actions.unshift(
        <Button
          aria-label={feedback.buttonLabel}
          className="spectrum-Shell-feedback-button"
          key="feedback-button"
          onClick={this.showFeedbackTool}
          variant="cta"
        >
          <span className="spectrum-Shell-feedback-button">
            {feedback.buttonLabel}
          </span>
        </Button>
      );
    }

    return (
      <div className="spectrum-ShellContainer">
        <ShellHeader
          {...{
            actions,
            customEnvLabel,
            envLabel,
            modal,
            onSolutionClick,
            onWorkspaceChange,
            selectedWorkspaceId,
            size,
            solutionIcon,
            solutionShortTitle,
            solutionTitle,
            viewportTakeover,
            workspaces
          }}
        />
        <div className={classNames('spectrum-ShellContainer-content', {
          'can-takeover': supportsViewportTakeover,
          viewportTakeover
        })}>
          {children}
        </div>
      </div>
    );
  }
}

Shell.propTypes = {
  container: PropTypes.oneOfType([PropTypes.elementType, PropTypes.func, PropTypes.object]),
  currentActiveTenantId: PropTypes.string,
  customButtons: PropTypes.object,
  customSearch: PropTypes.shape({
    enabled: PropTypes.bool,
    onClick: PropTypes.func,
    open: PropTypes.bool
  }),
  helpCenterConfig: PropTypes.shape({
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        href: PropTypes.string,
        icon: PropTypes.string,
        label: PropTypes.string
      })
    )
  }),
  imsOrgs: PropTypes.arrayOf(
    PropTypes.shape({
      imsOrgId: PropTypes.string,
      imsOrgName: PropTypes.string,
      tenantId: PropTypes.string
    })
  ).isRequired,
  landingpage: PropTypes.shape({
    description: PropTypes.string,
    href: PropTypes.string,
    icon: PropTypes.string,
    iconReact: PropTypes.string,
    longname: PropTypes.string,
    name: PropTypes.string,
    visible: PropTypes.bool
  }),
  locale: PropTypes.string,
  onCustomButtonClick: PropTypes.func,
  onImsOrgChange: PropTypes.func,
  onSignOutClick: PropTypes.func,
  onSolutionClick: PropTypes.func,
  onUserProfileClick: PropTypes.func,
  onWorkspaceChange: PropTypes.func,
  pulse: PropTypes.shape({
    hasNewAnnouncement: PropTypes.bool,
    onPulseClose: PropTypes.func,
    onPulseOpen: PropTypes.func,
    onPulseUnreadChange: PropTypes.func,
    onPulseViewAllClick: PropTypes.func,
    pulseComponent: PropTypes.shape({
      flyoutComponent: PropTypes.func
    }),
    unreadNotificationsCount: PropTypes.number
  }),
  selectedImsOrg: PropTypes.string,
  selectedSubOrg: PropTypes.object,
  selectedWorkspaceId: PropTypes.string,
  services: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      enabled: PropTypes.bool,
      href: PropTypes.string,
      icon: PropTypes.string,
      iconReact: PropTypes.string,
      learnMoreLink: PropTypes.string,
      longname: PropTypes.string,
      name: PropTypes.string,
      visible: PropTypes.bool
    })
  ),
  showBadge: PropTypes.bool,
  showLearnMoreModal: PropTypes.bool,
  solutionIcon: PropTypes.string,
  solutions: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      enabled: PropTypes.bool,
      href: PropTypes.string,
      icon: PropTypes.string,
      iconReact: PropTypes.string,
      learnMoreLink: PropTypes.string,
      longname: PropTypes.string,
      name: PropTypes.string,
      visible: PropTypes.bool
    })
  ),
  solutionShortTitle: PropTypes.string,
  solutionTitle: PropTypes.string,
  userAvatarUrl: PropTypes.string,
  userFirstName: PropTypes.string,
  userLastName: PropTypes.string,
  userName: PropTypes.string,
  userTitle: PropTypes.string,
  workspaces: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string
    })
  )
};

const NOOP = () => {};

Shell.defaultProps = {
  container: document.body,
  currentActiveTenantId: '',
  customButtons: [],
  customSearch: {
    enabled: false,
    onClick: () => {},
    open: false
  },
  helpCenterConfig: {},
  landingpage: {},
  locale: 'en-US',
  onCustomButtonClick: NOOP,
  onImsOrgChange: NOOP,
  onSignOutClick: NOOP,
  onSolutionClick: NOOP,
  onUserProfileClick: NOOP,
  onWorkspaceChange: NOOP,
  pulse: {
    hasNewAnnouncement: false,
    onPulseClose: NOOP,
    onPulseOpen: NOOP,
    onPulseUnreadChange: NOOP,
    onPulseViewAllClick: NOOP,
    pulseComponent: {
      flyoutComponent: NOOP
    },
    unreadNotificationsCount: 0
  },
  selectedWorkspaceId: '',
  services: [],
  showBadge: false,
  showLearnMoreModal: false,
  solutions: [],
  solutionShortTitle: 'AEC',
  solutionTitle: 'Adobe Experience Cloud',
  userAvatarUrl: undefined,
  userName: 'Adobe User',
  userTitle: undefined,
  workspaces: []
};

export default withLocalization(Shell);
